import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import ActionButton from "../widgets/ActionButton";
import OptionSelect from "../widgets/OptionSelect";
import { GraphQLGridField } from "../widgets/GraphQLDataGrid";
import SectionSelect from "./SectionSelect";

// Everything a saved query holds, as the form works on it. The columns are the field names that
// are visible, which is how the grid stores them.
export type SavedQueryDraft = {
  name: string;
  section: string;
  query: string;
  adminOnly: boolean;
  columns: string[];
};

// Everything about a saved query that can be changed — its name, its section, its filter, its
// columns and who may see it — in one form, for a query being saved from a grid as for one being
// edited. A query needs a name to be saved; without a section it is listed above the sections.
//
// What is being edited stays in here. The page behind this dialog holds a data grid, so a character
// that re-rendered the page re-rendered the grid with it and the field crawled; keeping the draft
// local means a keystroke redraws a text box and nothing else. Nothing reaches the backend until
// Save, and the mount is fresh each time the dialog is opened, so Cancel leaves no trace.
const SavedQueryDialog = ({
  title,
  initial,
  fields,
  saving,
  onClose,
  onSave,
}: {
  title: string;
  initial: SavedQueryDraft;
  fields: GraphQLGridField[];
  saving?: boolean;
  onClose: () => void;
  onSave: (draft: SavedQueryDraft) => void;
}) => {
  const [draft, setDraft] = useState(initial);
  const confirm = useConfirm();

  const change = (part: Partial<SavedQueryDraft>) =>
    setDraft({ ...draft, ...part });

  const unchanged =
    draft.name === initial.name &&
    draft.section === initial.section &&
    draft.query === initial.query &&
    draft.adminOnly === initial.adminOnly &&
    draft.columns.join() === initial.columns.join();

  // The columns are chosen as the labels the grid shows, not as the field names it stores.
  const selected = fields.filter((field) => draft.columns.includes(field.field));

  const handleSave = async () => {
    // Who can see the tile is the one change worth stopping for, and only when it is really being
    // changed — so the warning belongs here, on Save, rather than under the switch.
    if (draft.adminOnly !== initial.adminOnly) {
      try {
        await confirm({
          title: "Change visibility",
          description: draft.adminOnly
            ? "This query will be visible only to admins."
            : "This query will be visible to all.",
          confirmationText: "Save",
        });
      } catch {
        return; // dismissed: material-ui-confirm rejects rather than resolving false
      }
    }
    onSave(draft);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={draft.name}
            onChange={(event) => change({ name: event.target.value })}
            fullWidth
            autoFocus
            required
          />
          <SectionSelect
            value={draft.section}
            onChange={(section) => change({ section })}
          />
          <TextField
            label="Query"
            value={draft.query}
            onChange={(event) => change({ query: event.target.value })}
            multiline
            minRows={4}
            fullWidth
            sx={{ "& .MuiInputBase-input": { wordBreak: "break-all" } }}
          />
          <OptionSelect
            multiple
            label="Columns"
            options={fields}
            value={selected}
            getOptionLabel={(field) => field.label ?? field.field}
            isOptionEqualToValue={(option, value) => option.field === value.field}
            onChange={(_, chosen) =>
              change({ columns: chosen.map((field) => field.field) })
            }
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={draft.adminOnly}
                onChange={(event) => change({ adminOnly: event.target.checked })}
              />
            }
            label="Admin only"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <ActionButton
          onClick={handleSave}
          disabled={saving || unchanged || !draft.name.trim()}
        >
          Save
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default SavedQueryDialog;
